/**
 * User Agreement (Terms of Service & Privacy Policy)
 *
 * To update the agreement text, replace the AGREEMENT_TEXT string below
 * with your actual Terms of Service content.
 *
 * Conversion tips for a .docx file:
 *   - Word:   Open → Ctrl+A → Ctrl+C → paste below (clean up smart-quotes if needed)
 *   - Pandoc: pandoc your-tos.docx -t plain -o tos.txt  →  copy tos.txt here
 *   - Node:   npx mammoth your-tos.docx --output-format=markdown  →  copy output here
 *
 * After updating the text, increment AGREEMENT_VERSION so returning users are
 * shown the new agreement on next sign-in (Phase 4, not yet implemented).
 */

export const AGREEMENT_VERSION = '1.0';

export const AGREEMENT_TEXT = `Child Nav Family App
Terms of Service, End User License Agreement & Privacy Notice
Effective Date: 5/1/2026  |  Version 1.0
PLEASE READ CAREFULLY. By creating an account or using the Child Nav Family App, you agree to be bound by these Terms of Service, End User License Agreement, and Privacy Notice (collectively, this “Agreement”). If you do not agree, do not install, access, or use the App.

1.  Acceptance of Terms
This Agreement is a legally binding contract between you (“User,” “you,” or “your”) and Child Nav, Inc. (“Child Nav,” “we,” “our,” or “us”), a corporation organized under the laws of Colorado. By tapping “Agree & Continue” during registration, or by otherwise accessing or using the Child Nav Family App (the “App”), you:
•	Are at least 18 years of age and have full legal authority to enter into this Agreement;
•	Are the parent, legal guardian, or authorized caregiver of the child or children whose developmental information you will enter;
•	Accept all terms, conditions, and notices contained herein; and
•	Agree to the collection and use of information as described in this Agreement.

If you are entering into this Agreement on behalf of an organization, you represent that you have authority to bind that organization.

2.  Nature of the Service — Informational Use Only
IMPORTANT — NOT MEDICAL ADVICE: Child Nav is a developmental monitoring tool, not a medical device. Content, milestone screenings, developmental scores, and any output generated by the App are for informational and educational purposes only and do not constitute medical advice, diagnosis, or treatment.

2.1  Scope of Information Provided
The App provides:
•	Developmental milestone tracking across cognitive, communication, fine motor, gross motor and social-emotional domains;
•	Probability-based developmental screening scores derived from evidence-based methodologies including Item Response Theory (IRT);
•	Educational guidance, activity suggestions, and curated resources from public-domain and licensed clinical sources; and
•	Well-visit preparation summaries intended as conversation aids for healthcare appointments.

2.2  Limitations
You expressly acknowledge and agree that:
•	The App does not replace, and is not a substitute for, the professional judgment, diagnosis, or treatment recommendations of a licensed healthcare provider, pediatrician, developmental pediatrician, psychologist, or other qualified clinician;
•	Developmental screening results generated by the App are probabilistic indicators only and may not accurately reflect a child’s actual developmental status;
•	A screening result that falls within or outside of expected ranges does not confirm or rule out any developmental condition, disorder, or delay;
•	You should always consult a qualified healthcare professional before making any decision based on information provided by the App; and
•	The App is not cleared or approved by the U.S. Food and Drug Administration (FDA) as a medical device.

2.3  Limitation of Liability for Use of Results
To the fullest extent permitted by applicable law, Child Nav shall not be liable for any action taken, or not taken, by you or any third party in reliance on information, scores, guidance, or any other output generated by the App. You assume full responsibility for how you use, share, or act upon any information derived from the App.

3.  End User License Agreement (EULA)
3.1  Grant of License
Subject to your compliance with this Agreement, Child Nav grants you a limited, non-exclusive, non-transferable, revocable license to download and use the App on a personal mobile device that you own or control, solely for your personal, non-commercial use in monitoring the development of your own child or a child for whom you are the legal caregiver.

3.2  Restrictions
You may not:
•	Copy, modify, adapt, translate, reverse engineer, disassemble, decompile, or create derivative works of the App or any portion thereof;
•	Rent, lease, loan, resell, sublicense, distribute, or otherwise transfer the App or your rights under this Agreement to any third party;
•	Remove, alter, or obscure any proprietary notices, labels, or marks on the App;
•	Use the App for any commercial purpose, including offering developmental screening services to others using our platform;
•	Access the App through automated means, bots, scrapers, or data harvesting tools;
•	Use the App in any manner that could damage, disable, overburden, or impair our servers or networks; or
•	Attempt to gain unauthorized access to any portion of the App, our systems, or networks.

3.3  Intellectual Property
The App, including but not limited to its software, algorithms, scoring methodology, IRT-based developmental age calculations, Integrated Pediatric Screening (IPS) network architecture, content, design, trademarks, and all related proprietary materials, are and shall remain the exclusive property of Child Nav, Inc. or its licensors. This Agreement does not convey to you any ownership interest in the App. All rights not expressly granted herein are reserved.

3.4  Updates and Changes
Child Nav may, in its sole discretion, update, modify, suspend, or discontinue the App or any features thereof at any time with or without notice. Continued use of the App following any update constitutes your acceptance of the modified App.

3.5  Third-Party Content and Services
The App may contain links to third-party websites, resources, or services. Child Nav does not endorse and is not responsible for the content, accuracy, or practices of any third-party sites or services. Your interactions with third-party services are solely between you and those third parties.

4.  Privacy, Protected Health Information & Data Use
HIPAA NOTICE: Child Nav is committed to protecting the privacy and security of health information you provide. To the extent the App collects Protected Health Information (PHI) as defined under the Health Insurance Portability and Accountability Act of 1996 (HIPAA), that information is handled in accordance with applicable federal and state law.

4.1  Information We Collect
We collect the following categories of information when you use the App:

Account Information
•	Name and email address;
•	Child’s date of birth or age range; and
•	Relationship of the user to the child (parent, guardian, caregiver).

Developmental & Health Information (PHI)
•	Milestone observations and assessment responses you enter for your child;
•	Developmental screening scores and domain summaries;
•	Parental concerns and notes you submit within the App;
•	Well-visit preparation summaries and provider communication artifacts; and
•	Any additional health or developmental information you voluntarily provide.

Usage & Technical Data
•	Device identifiers, operating system, and App version;
•	Log data including session timestamps and feature interactions; and
•	Crash reports and performance diagnostics.

4.2  How We Use Your Information
We use information collected through the App for the following purposes:
•	To provide, operate, and improve the App and its features;
•	To calculate developmental age estimates and generate screening outputs personalized to your child;
•	To send you App-related notifications, reminders, and educational content;
•	To respond to your support requests; and
•	To comply with legal obligations.

4.3  Protection of Protected Health Information
Child Nav implements administrative, technical, and physical safeguards to protect PHI, including:
•	AES-256 encryption of PHI at rest;
•	TLS 1.2 or higher encryption of data in transit;
•	Access controls limiting PHI access to authorized personnel with a business need;
•	Audit logging of access to PHI systems; and
•	Business Associate Agreements (BAAs) with any third-party vendors that process PHI on our behalf.

We will not sell, rent, or disclose PHI to third parties for marketing or commercial purposes without your explicit consent, except as required by law or as described in this Agreement.

4.4  De-Identified Data — Research and Population Norms
With your consent, Child Nav may de-identify your data in accordance with the HIPAA Expert Determination or Safe Harbor de-identification standards (45 C.F.R. § 164.514). De-identified data carries no reasonable basis for identifying you or your child and may be used for:
•	Academic and clinical research to advance the science of developmental monitoring;
•	Calibration and refinement of our population normative databases and IRT-based scoring models;
•	Quality assurance and algorithm improvement; and
•	Publication of aggregate, anonymized population-level findings.

OUR COMMITMENT: De-identified data will NEVER be sold to commercial third parties, advertisers, data brokers, or used for targeted marketing. Its use is strictly limited to improving child developmental science and the accuracy of the App.

You may opt out of the use of your de-identified data for research purposes at any time through the App’s Privacy Settings without affecting your access to the App.

4.5  Sharing of Information
We may share your information in the following limited circumstances:
•	With Your Healthcare Providers: If you use the well-visit sharing feature, summary reports will be shared only with the providers you explicitly authorize;
•	With Service Providers: We engage third-party vendors to operate the App under contractual data processing agreements, including BAAs where required;
•	For Legal Compliance: We may disclose information if required by law, court order, or government authority;
•	In a Business Transaction: If Child Nav is acquired, merged, or undergoes a similar transaction, your information may be transferred, provided the successor entity honors the commitments in this Agreement; and
•	With Your Consent: For any other purpose, with your explicit prior consent.

4.6  Data Retention
We retain your PHI and account data for as long as your account is active, or as necessary to fulfill the purposes described in this Agreement, comply with legal obligations, resolve disputes, or enforce our agreements. You may request deletion of your account and associated data at any time (see Section 8).

4.7  Children’s Privacy (COPPA)
The App is designed for use by parents and adult caregivers. We do not knowingly permit children under the age of 13 to create accounts or directly submit personal information. Developmental data entered on behalf of a child is submitted by the adult caregiver account holder. If you believe a child under 13 has directly created an account, please contact us immediately at privacy@childnav.com.

4.8  California Privacy Rights (CCPA/CPRA)
If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), including the right to know, right to delete, right to correct, right to opt out of sale or sharing, and right to non-discrimination. To exercise these rights, contact us at privacy@childnav.com. We do not sell personal information as defined under California law.

4.9  State Health Data Privacy
Several states have enacted comprehensive health data privacy laws that may apply to information you share with the App. Where applicable, Child Nav will comply with state-specific health data privacy requirements. Please contact us at privacy@childnav.com for jurisdiction-specific inquiries.

5.  Account Registration and Security
5.1  Account Creation
To use the App, you must create an account by providing accurate, current, and complete information. You agree to update your account information to keep it accurate.

5.2  Account Security
You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to:
•	Use multi-factor or passkey authentication;
•	Notify us immediately at support@childnav.com of any unauthorized use of your account; and
•	Sign out of your account after each session on a shared device.

Child Nav is not liable for any loss or damage arising from your failure to maintain account security.

5.3  One Account Per Family
You may use one account per family unit. You may share access to your account with a co-parent or co-guardian at your own discretion. You may not share your account credentials with individuals outside your immediate family unit.

6.  User Responsibilities and Prohibited Conduct
You agree to use the App only for lawful purposes and in a manner consistent with its intended use. You may not:
•	Enter false, misleading, or fraudulent information about yourself or a child;
•	Impersonate any person, organization, or healthcare provider;
•	Use the App to harass, threaten, or harm any person;
•	Attempt to circumvent any security measures or access controls;
•	Upload or transmit viruses, malware, or other harmful code;
•	Use the App in violation of any applicable federal, state, or local law or regulation; or
•	Interfere with other users’ use and enjoyment of the App.

7.  Disclaimers of Warranties
DISCLAIMER: THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, CHILD NAV DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

Child Nav does not warrant that:
•	The App will be uninterrupted, error-free, or free of viruses or other harmful components;
•	Developmental screening results will be accurate, complete, or appropriate for any particular child;
•	The App will meet your specific requirements or expectations; or
•	Any errors in the App will be corrected.

8.  Limitation of Liability
TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:

•	IN NO EVENT SHALL CHILD NAV, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, OR SERVICE PROVIDERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING LOSS OF PROFITS, GOODWILL, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF, OR INABILITY TO USE, THE APP;
•	CHILD NAV’S TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THIS AGREEMENT OR YOUR USE OF THE APP SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO CHILD NAV IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) FIFTY DOLLARS ($50.00);
•	YOU ACKNOWLEDGE THAT THE LIMITATIONS IN THIS SECTION ARE AN ESSENTIAL ELEMENT OF THE BASIS OF THE BARGAIN BETWEEN YOU AND CHILD NAV, AND THAT CHILD NAV WOULD NOT PROVIDE THE APP WITHOUT THESE LIMITATIONS; AND
•	SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF THESE LAWS APPLY TO YOU, SOME OR ALL OF THE ABOVE EXCLUSIONS OR LIMITATIONS MAY NOT APPLY, AND YOU MAY HAVE ADDITIONAL RIGHTS.

9.  Indemnification
You agree to indemnify, defend, and hold harmless Child Nav, Inc. and its officers, directors, employees, contractors, agents, licensors, and successors from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys’ fees) arising out of or in connection with:
•	Your use of or access to the App;
•	Your violation of any provision of this Agreement;
•	Your violation of any third-party rights, including intellectual property or privacy rights; or
•	Any action you take or fail to take based on information provided by the App.

10.  Account Termination and Data Deletion
10.1  Termination by You
You may delete your account at any time through the App’s Settings > Account > Delete Account. Upon account deletion:
•	Your personal account information and child profiles will be permanently deleted;
•	PHI associated with your account will be deleted or de-identified in accordance with applicable law and our retention obligations;
•	De-identified data already incorporated into population-level research datasets may be retained in aggregate form but cannot be re-identified or linked back to your account; and
•	Provider-shared reports previously transmitted to healthcare providers may be retained by those providers in accordance with their own records policies.

10.2  Termination by Child Nav
Child Nav may suspend or terminate your account at any time, with or without notice, if we reasonably believe you have violated this Agreement, engaged in fraudulent activity, or posed a security risk to other users or our systems.

10.3  Survival
Sections 2 (Nature of Service), 3.3 (Intellectual Property), 4 (Privacy), 7 (Disclaimers), 8 (Limitation of Liability), 9 (Indemnification), and 12 (Governing Law) shall survive termination of this Agreement.

11.  Push Notifications and Communications
By creating an account, you consent to receive:
•	In-app notifications related to your child’s milestone tracking, upcoming activities, and well-visit preparation;
•	Push notifications related to App updates, new features, and important account notices; and
•	Transactional emails related to your account.

You may manage notification preferences in the App’s Settings at any time. We will not send unsolicited commercial email without your affirmative consent.

12.  Governing Law and Dispute Resolution
12.1  Governing Law
This Agreement shall be governed by and construed in accordance with the laws of the State of Colorado, without regard to its conflict of law provisions.

12.2  Informal Resolution
Before initiating any formal dispute, you agree to contact Child Nav at legal@childnav.com and provide written notice of your claim, and to make a good-faith effort to resolve the dispute informally for at least 30 days.

12.3  Binding Arbitration
If informal resolution is unsuccessful, any dispute, claim, or controversy arising from or related to this Agreement or your use of the App shall be resolved through binding individual arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules. The arbitration shall be conducted in Denver, Colorado or remotely, at the parties’ election. The arbitrator’s decision will be final and binding.

12.4  Class Action Waiver
YOU AND CHILD NAV AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, REPRESENTATIVE, OR COLLECTIVE ACTION. If this waiver is found unenforceable for a particular claim, that claim shall proceed in court, but any remaining claims remain subject to arbitration.

12.5  Exceptions
Either party may seek emergency injunctive relief in a court of competent jurisdiction to protect intellectual property rights or confidential information, pending arbitration.

13.  Modifications to this Agreement
Child Nav reserves the right to modify this Agreement at any time. When we make material changes, we will:
•	Display a notice within the App at least 14 days before the changes take effect;
•	Send an email notification to your registered email address for significant changes; and
•	Update the “Effective Date” at the top of this Agreement.

Your continued use of the App after the effective date of any modification constitutes your acceptance of the updated Agreement. If you do not agree to the modified Agreement, you must stop using the App and may delete your account.

14.  General Provisions
14.1  Entire Agreement
This Agreement constitutes the entire agreement between you and Child Nav with respect to the App and supersedes all prior agreements, understandings, negotiations, and discussions, whether oral or written.

14.2  Severability
If any provision of this Agreement is found to be invalid, illegal, or unenforceable, that provision shall be modified to the minimum extent necessary to make it valid and enforceable, and the remaining provisions shall continue in full force and effect.

14.3  Waiver
Failure by Child Nav to enforce any right or provision of this Agreement shall not constitute a waiver of that right or provision.

14.4  Assignment
You may not assign or transfer this Agreement or any of your rights or obligations under it without our prior written consent. Child Nav may assign this Agreement freely, including in connection with a merger, acquisition, or sale of assets.

14.5  Force Majeure
Child Nav shall not be liable for any failure or delay in performance due to causes beyond its reasonable control, including acts of God, natural disasters, pandemic, war, government actions, or internet or telecommunications outages.

14.6  Contact Information
For questions about this Agreement, contact:

Child Nav, Inc.
Attn: Privacy & Legal
Email: legal@childnav.com
Privacy: privacy@childnav.com
Website: www.childnav.com

15.  User Acknowledgment and Acceptance
BY TAPPING “AGREE & CONTINUE”: You confirm that you have read, understood, and agree to be bound by these Terms of Service, End User License Agreement, and Privacy Notice. You confirm you are at least 18 years old and are the parent, legal guardian, or authorized caregiver of the child(ren) whose information will be entered in the App.

Your acceptance is recorded with:
•	Your account email address;
•	The date and timestamp of acceptance; and
•	The version number of the Agreement in effect at time of acceptance.

A copy of the version of this Agreement you accepted is available at any time in the App under Settings > Legal > Terms of Service.

— End of Agreement —
Document Version: 1.0  |  Effective Date: 5/1/2026

`.trim();
